import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import EditSnackbar from 'bento-ordering/base/components/Snackbar/EditSnackbar';
import SuccessfulAddSnackbar from 'catering/components/SuccessfulAddSnackbar';
import SecondaryCta from 'components/Buttons/SecondaryCta';
import { ScreenContainer } from 'components/Containers';
import Footer from 'components/Footer';
import SeoTitleUpdater from 'components/SeoTitleUpdater';
import MenuHero from 'catering/components/Menu/MenuHero';
import MenuSection from 'catering/components/Menu/MenuSection';
import MenuSectionsBarItems from 'components/Menu/MenuSectionsBarItems';
import PageLoadSpinner from 'components/PageLoadSpinner';
import { getDateTimeFromFulfillmentDateAndTime } from 'catering/helpers';

import NoSearchResults from 'bento-ordering/menus/components/NoSearchResults';
import { useMenu } from 'catering/context/CateringMenuContext';
import { useAccount } from 'catering/context/providers/AccountProvider';
import {
  useCateringLocations,
  useCart,
  usePrefetchMenuDetails,
  selectCateringLocationBySlug,
  useCateringLocationMenuAvailability,
  useCateringLocationFirstAvailableFulfillmentHours,
  useCateringLocationInventory
} from 'catering/hooks/query';
import { useGetUpsellItems } from 'catering/hooks';
import { aggregateLocationInventory } from 'catering/helpers/aggregateCateringLocationInventory';
import {
  useRedirectToOrderSettingsForNoFulfillmentSettings,
  useRedirectToLocationOffline
} from 'catering/hooks/navigation';
import { MENU_LAYOUT_ID } from 'constants/layout';
import {
  getButtonBackgroundHoverColor,
  getItemDescriptionsAndPricesTypography
} from 'styles/helpers/getThemeStyles';
import { isSoleEntity } from 'bento-ordering/base/reducers/bentoApi';

import MenuNestedRoutes from './MenuNestedRoutes';
import HonestPricingLawBanner from 'base-ordering/components/HonestPricingLawBanner';
import { mapCartToServiceFeeType } from 'catering/helpers/HonestPricingLaw';

function MenuScreenContents({
  cart,
  menu,
  isUpsellEnabled,
  upsellItems,
  location,
  locationInventory,
  shouldDisplayMenuLink,
  searchTerm,
  setSearchTerm,
  numberOfMenuItems
}) {
  const {
    charge_custom_fee: chargeCustomFee,
    charge_service_fee: chargeServiceFee,
    custom_fee_amount: customFeeAmount,
    custom_fee_flat_or_percent: customFeeFlatOrPercent,
    custom_fee_name: customFeeName
  } = location;
  const MARGIN_OFFSET = 125;
  const { utmFooterLink, displayLocationInformationOnLayout } = useAccount();
  const [isSearchInputOpen, setSearchInputOpenStatus] = React.useState(false);

  const isSingleLocation = isSoleEntity(location);

  const fulfillmentDateTime = getDateTimeFromFulfillmentDateAndTime(
    cart.fulfillment_date,
    cart.fulfillment_time
  );

  const { bannerFeeText } = mapCartToServiceFeeType(
    cart.display_service_fee,
    chargeCustomFee,
    chargeServiceFee,
    customFeeName,
    customFeeAmount,
    customFeeFlatOrPercent
  );

  const ownFooterState = theme => {
    const fontStyles = getItemDescriptionsAndPricesTypography(theme);
    return {
      color: getButtonBackgroundHoverColor(theme),
      ...fontStyles
    };
  };

  const renderMenuSections = () => {
    let content = null;
    if (menu.sections.length) {
      content = menu.sections.map(menuSection => (
        <MenuSection
          key={menuSection.id}
          section={menuSection}
          shouldLazyLoadMenu={menu.shouldLazyLoad}
          fulfillmentDateTime={fulfillmentDateTime}
          menuLeadTime={cart.menu_lead_time}
          fulfillmentMethod={cart.catering_fulfillment_method}
          locationInventory={locationInventory}
        />
      ));
    } else if (searchTerm) {
      const shouldShowPopularItemsSection =
        isUpsellEnabled && !!upsellItems.length;
      const popularItemsSection = {
        name: 'Popular items',
        items: upsellItems,
        description: ''
      };

      content = (
        <>
          <NoSearchResults searchTerm={searchTerm} />
          {shouldShowPopularItemsSection && (
            <MenuSection
              section={popularItemsSection}
              fulfillmentDateTime={fulfillmentDateTime}
              menuLeadTime={cart.menu_lead_time}
              fulfillmentMethod={cart.catering_fulfillment_method}
              locationInventory={locationInventory}
            />
          )}
        </>
      );
    }

    return content;
  };

  return (
    <div>
      <SeoTitleUpdater
        section={isSingleLocation ? null : 'Locations'}
        locationName={`${location.location.name} Menu`}
      />
      <MenuHero
        cart={cart}
        menu={menu}
        location={location}
        displayLocationInformationOnLayout={displayLocationInformationOnLayout}
        shouldDisplayMenuLink={shouldDisplayMenuLink}
      />
      <MenuSectionsBarItems
        marginOffset={MARGIN_OFFSET}
        menu={menu}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isSearchInputOpen={isSearchInputOpen}
        handleOpenSearchInput={setSearchInputOpenStatus}
        handleCloseSearchInput={setSearchInputOpenStatus}
        numberOfMenuItems={numberOfMenuItems}
      />
      {cart.should_show_transparent_fees && (
        <HonestPricingLawBanner bannerFeeText={bannerFeeText} />
      )}
      <ScreenContainer id={MENU_LAYOUT_ID}>
        {renderMenuSections()}
      </ScreenContainer>
      <Footer>
        <SecondaryCta
          label="Powered By Bentobox"
          component="a"
          href={utmFooterLink}
          dataTestId="footer"
          ownState={{
            footer: ownFooterState
          }}
        />
      </Footer>
    </div>
  );
}

MenuScreenContents.propTypes = {
  cart: PropTypes.shape({
    fulfillment_date: PropTypes.string.isRequired,
    fulfillment_time: PropTypes.string.isRequired,
    catering_fulfillment_method: PropTypes.string.isRequired,
    menu_lead_time: PropTypes.shape({
      lead_time_days: PropTypes.number,
      lead_time_hours: PropTypes.number,
      cutoff_time: PropTypes.string
    }).isRequired,
    should_show_transparent_fees: PropTypes.bool.isRequired,
    display_service_fee: PropTypes.string.isRequired
  }),
  menu: PropTypes.shape({
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      })
    ),

    shouldLazyLoad: PropTypes.bool
  }),
  isUpsellEnabled: PropTypes.bool.isRequired,
  upsellItems: PropTypes.array.isRequired,
  location: PropTypes.object,
  locationInventory: PropTypes.object,
  shouldDisplayMenuLink: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  numberOfMenuItems: PropTypes.number.isRequired
};

function MenuScreen() {
  const { locationSlug } = useParams();
  useRedirectToLocationOffline();
  useRedirectToOrderSettingsForNoFulfillmentSettings();

  const { isLoading: isCartLoading, data: cart } = useCart();

  const fulfillmentDateTime =
    cart?.fulfillment_date && cart?.fulfillment_time
      ? getDateTimeFromFulfillmentDateAndTime(
          cart.fulfillment_date,
          cart.fulfillment_time
        )
      : null;

  const {
    isLoading: isLoadingCateringLocation,
    data: cateringLocation
  } = useCateringLocations({
    select: locations => selectCateringLocationBySlug(locations, locationSlug)
  });

  const {
    menu,
    isMenuLoading,
    searchTerm,
    setSearchTerm,
    numberOfMenuItems
  } = useMenu();

  const {
    isLoading: isLoadingMenuAvailability,
    data: allAvailableLocationMenus
  } = useCateringLocationMenuAvailability({ locationSlug });

  const {
    data: availableHours
  } = useCateringLocationFirstAvailableFulfillmentHours({
    locationSlug
  });

  usePrefetchMenuDetails();

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useCateringLocationInventory({
    locationSlug,
    fulfillmentDateTime: fulfillmentDateTime
  });

  let locationInventory = { location_id: '', items: [] };
  if (hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  } else if (data?.pages) {
    locationInventory = aggregateLocationInventory(data, locationInventory);
  }

  const { isUpsellEnabled, upsellItems } = useGetUpsellItems(
    cart,
    locationInventory
  );

  const [alertContent, setAlertContent] = React.useState({
    isOpen: false
  });

  const handleCloseAlert = () => setAlertContent({ isOpen: false });

  const [editAlertContent, setEditAlertContent] = React.useState({
    isOpen: false
  });

  const handleCloseEditAlert = () => setEditAlertContent({ isOpen: false });

  if (
    isMenuLoading ||
    !menu ||
    isLoadingCateringLocation ||
    isCartLoading ||
    isLoadingMenuAvailability
  ) {
    return <PageLoadSpinner />;
  }

  function getFulfillmentMethodFromLocationOrCart() {
    const firstAvailableFulfillmentMethod =
      availableHours?.all?.catering_fulfillment_method;

    return cart?.catering_fulfillment_method || firstAvailableFulfillmentMethod;
  }

  const shouldDisplayMenuLink = () => {
    const fulfillmentMethod = getFulfillmentMethodFromLocationOrCart();
    const numberOfLocationMenus =
      allAvailableLocationMenus[fulfillmentMethod].length;
    return numberOfLocationMenus > 1;
  };

  return (
    <>
      <MenuScreenContents
        cart={cart}
        menu={menu}
        isUpsellEnabled={isUpsellEnabled}
        upsellItems={upsellItems}
        location={cateringLocation}
        locationInventory={locationInventory}
        shouldDisplayMenuLink={shouldDisplayMenuLink()}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        numberOfMenuItems={numberOfMenuItems}
      />
      <MenuNestedRoutes
        setAlertContent={setAlertContent}
        setEditAlertContent={setEditAlertContent}
      />
      {alertContent.isOpen && (
        <SuccessfulAddSnackbar
          item={alertContent.item.name}
          addedItemImage={alertContent.item.image}
          cartQuantity={alertContent.cart.quantity}
          subtotal={alertContent.cart.subtotal}
          cartItems={alertContent.cart.items}
          locationSlug={alertContent.locationSlug}
          onClose={handleCloseAlert}
        />
      )}
      {editAlertContent.isOpen && (
        <EditSnackbar
          open={editAlertContent.isOpen}
          item={editAlertContent.itemName}
          onClose={handleCloseEditAlert}
          errorState={editAlertContent.errorState}
        />
      )}
    </>
  );
}

export default MenuScreen;
